/**
 * Creation and edition of a made-to-measure event (Villa Dior) with its
 * thumbnail, sent as multipart with the fields.
 */
import { useState, type ChangeEvent, type SubmitEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'sonner';
import { Dialog } from '@/shared/ui/Dialog';
import uploadIcon from '@/assets/images/upload.svg';
import closeIcon from '@/assets/images/close.svg';
import { createDmsEvent, updateDmsEvent, type DmsEvent } from './api';
import {
  EVENT_PICTURE_MAX_KB,
  eventFormValues,
  eventPictureUrl,
  formToEvent,
  validateEventForm,
  type EventFormErrors,
  type EventFormValues,
} from './event-form';
import { readAsDataUrl } from '@/shared/lib/read-file';
import { kebabCase } from '@/shared/lib/kebab-case';

export interface EventDialogProps {
  item: DmsEvent | null;
  onClose: (result: 'Confirm' | 'Cancel') => void;
}

export function EventDialog({ item, onClose }: Readonly<EventDialogProps>) {
  const creating = !item;
  const [values, setValues] = useState<EventFormValues>(() =>
    eventFormValues(item),
  );
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<EventFormErrors>({});
  const [dirty, setDirty] = useState<Set<keyof EventFormValues>>(
    () => new Set(),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [picture, setPicture] = useState<{
    file: File;
    base64: string | null;
  } | null>(null);
  const serverPicture = eventPictureUrl(item);

  const set = <K extends keyof EventFormValues>(
    key: K,
    value: EventFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setDirty((prev) => new Set(prev).add(key));
  };
  const liveErrors = validateEventForm(values);
  const invalidClass = (key: keyof EventFormValues) =>
    dirty.has(key) && liveErrors[key] ? 'invalid' : '';
  const fieldError = (key: keyof EventFormValues) => submitted && errors[key];

  const onPictureChosen = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size / 1024 > EVENT_PICTURE_MAX_KB) {
      // Historical message, kept as-is although the limit is 5 MB (see migration.md).
      toast.error('File should be smaller than 500kb');
      return;
    }
    setPicture({ file, base64: null });
    const base64 = await readAsDataUrl(file);
    setPicture((prev) => (prev?.file === file ? { file, base64 } : prev));
  };

  const save = useMutation({
    mutationFn: () => {
      const event = formToEvent(values, item, !!picture);
      return creating
        ? createDmsEvent(event, picture?.file ?? null)
        : updateDmsEvent(item.id, event, picture?.file ?? null);
    },
    onSuccess: () => onClose('Confirm'),
    onError: (error: Error) =>
      setErrorMessage(
        error.message ||
          (creating ? 'Failed to create event' : 'Failed to update event'),
      ),
  });

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrorMessage(null);
    const nextErrors = validateEventForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setErrorMessage('Please fix the errors');
      return;
    }
    save.mutate();
  };

  const testId = (key: string) => `mtm-event-form-${kebabCase(key)}`;

  const field = (
    key: keyof EventFormValues,
    label: string,
    type: 'text' | 'date' = 'text',
  ) => (
    <div className="mb-6">
      <label className="field-label" htmlFor={`event-${key}`}>
        {label}
      </label>
      <input
        id={`event-${key}`}
        data-testid={testId(key)}
        className={`field ${invalidClass(key)}`}
        name={key}
        type={type}
        placeholder={label}
        value={values[key]}
        onChange={(event) => set(key, event.target.value)}
      />
      {key !== 'storeCode' && (
        <small hidden={!fieldError(key)} data-testid={`${testId(key)}-error`}>
          {errors[key]}
        </small>
      )}
    </div>
  );

  return (
    <Dialog
      title={creating ? 'Add an event' : 'Edit an event'}
      onClose={() => onClose('Cancel')}
      testId="mtm-event-dialog"
    >
      <form className="form popin-form" onSubmit={onSubmit} noValidate>
        <div className="bs-row">
          <div className="col-span-4">
            {field('name', 'Name')}
            {field('location', 'Location')}
            {field('eventId', 'Event Id')}
          </div>
          <div className="col-span-4">
            {field('startDate', 'Start Date', 'date')}
            {field('endDate', 'End Date', 'date')}
            {field('storeCode', 'Main store Code')}
          </div>
          <div className="col-span-4">
            <div className="mb-6">
              <span className="field-label" id="event-picture-label">
                Picture
              </span>
              <input
                className="field field-file-input"
                id="event-picture"
                data-testid="mtm-event-form-picture-input"
                type="file"
                accept=".png, .jpg, .jpeg, .gif, .webp"
                aria-labelledby="event-picture-label"
                onChange={(event) => void onPictureChosen(event)}
              />
              <label htmlFor="event-picture" className="field field-file-label">
                {picture?.base64 && (
                  <img
                    className="icon"
                    src={picture.base64}
                    alt=""
                    data-testid="mtm-event-form-picture-preview"
                  />
                )}
                {!picture && serverPicture && (
                  <img
                    className="icon"
                    src={serverPicture}
                    alt=""
                    data-testid="mtm-event-form-picture-preview"
                  />
                )}
                {!picture && !serverPicture && (
                  <img className="upload-icon" src={uploadIcon} alt="" />
                )}
                {picture && (
                  <button
                    type="button"
                    className="remove-icon"
                    aria-label="Remove picture"
                    data-testid="mtm-event-form-picture-remove-button"
                    onClick={(event) => {
                      event.preventDefault();
                      setPicture(null);
                    }}
                  >
                    <img src={closeIcon} alt="" />
                  </button>
                )}
              </label>
              <small className="field-hint">
                Leave empty to use the default event thumbnail.
              </small>
            </div>
          </div>
        </div>
        {errorMessage && (
          <div
            className="alert mb-0"
            role="alert"
            data-testid="mtm-event-form-error"
          >
            <span className="error-msg">{errorMessage}</span>
          </div>
        )}
        <div className="mt-6 flex justify-center">
          {save.isPending ? (
            <FontAwesomeIcon
              className="form-spinner"
              icon={faCircleNotch}
              spin
              aria-label="Saving"
            />
          ) : (
            <button
              className="button button-form"
              type="submit"
              data-testid="mtm-event-form-save-button"
            >
              Save
            </button>
          )}
        </div>
      </form>
    </Dialog>
  );
}
